import { useState } from 'react'
import { useDocumentsByChecklistItem, useFolders, useSetChecklistItemStatus } from '../api/rooms'
import { useAuth } from '../auth/AuthContext'
import type {
  ChecklistItemResponse,
  ChecklistResponse,
  ChecklistSectionResponse,
  RoomResponse,
  RoomStatus,
} from '../api/types'
import { checklistMoves, type ChecklistMove } from '../lib/checklist'
import { errorMessage, formatUtc } from '../lib/format'
import { dataPolicyBlocks } from '../lib/dataPolicy'
import { DocumentList } from '../screens/Room'
import {
  ItemAssignees,
  ItemFacts,
  ItemHistory,
  ItemUpload,
  ResponderNotes,
} from './ChecklistAccountability'

/**
 * One request, opened: the request in full with its lines kept, the requesting side's note,
 * why it was sent back or is not applicable, the moves this caller may make in this room's
 * state, and the documents that answer it. The moves come from `checklistMoves`; the API
 * decides again on the click, and is told the state this screen was showing so that two
 * people acting on one item find out.
 */
export function ChecklistItemDetail({
  roomId,
  roomStatus,
  item,
  checklist,
  room,
  section = null,
  onOpenRelated,
}: {
  roomId: string
  roomStatus: RoomStatus
  item: ChecklistItemResponse
  checklist: Pick<ChecklistResponse, 'canManage' | 'canSubmit' | 'canAssign'>
  /** The room as the caller sees it. With it, the Phase 2 parts are drawn (ADR 0009); without it, the panel is Phase 1's. */
  room?: RoomResponse
  section?: ChecklistSectionResponse | null
  onOpenRelated?: (itemId: string) => void
}) {
  const { me } = useAuth()
  // The history is an auditor's read: the role opens the door, the room's standing decides (as the Activity tab).
  const canReadHistory =
    room !== undefined &&
    ['auditor', 'room-owner', 'platform-admin'].some((r) => me?.roles.includes(r)) &&
    (room.memberRole === 'Owner' || room.permissions.viewAudit)
  const setStatus = useSetChecklistItemStatus(roomId)
  const [asking, setAsking] = useState<ChecklistMove | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const moves = checklistMoves(item, checklist, roomStatus)

  const move = (to: ChecklistMove, because?: string) => {
    setError(null)
    setStatus.mutate(
      { itemId: item.id, status: to.to, expectedStatus: item.status, reason: because },
      {
        onSuccess: () => {
          setAsking(null)
          setReason('')
        },
        onError: (e) => setError(errorMessage(e)),
      },
    )
  }

  return (
    <div className="item-detail">
      <ItemFacts roomId={roomId} item={item} onOpenRelated={onOpenRelated} />
      {item.requestText ? (
        // A <pre> styled from the stylesheet: the request's "i) ii) iii)" lines are content.
        <pre className="request-text">{item.requestText}</pre>
      ) : (
        <p className="muted small no-margin">No request text; the title is the request.</p>
      )}

      {item.providerNotes && (
        <div className="note note-provider">
          <p className="note-label">Note from the requesting side</p>
          <pre className="request-text request-text-note">{item.providerNotes}</pre>
        </div>
      )}
      {item.statusReason && (
        <div className={item.status === 'NeedsRevision' ? 'note note-warn' : 'note'}>
          <p className="note-label">
            {item.status === 'NeedsRevision' ? 'Sent back because' : 'Not applicable because'}
          </p>
          <p className="no-margin">{item.statusReason}</p>
        </div>
      )}
      {item.description && (
        <p className="small muted no-margin">Internal note: {item.description}</p>
      )}

      {room && (
        // NO key on the notes' stamp. It was there for a day (2026-09-20) "so the box is never stale", and it
        // was the bug: a refused save refetches the list, the stamp is then the OTHER person's, the key
        // changes, React remounts the box, and what this person typed is gone with no banner. The 409 is
        // the staleness detector; the box keeps its draft and shows theirs beside it.
        <ResponderNotes roomId={roomId} room={room} item={item} canSubmit={checklist.canSubmit} />
      )}

      {(item.submittedBy || item.completedBy) && (
        <p className="small muted no-margin">
          {item.submittedBy &&
            `Submitted by ${item.submittedBy} · ${formatUtc(item.submittedUtc)}. `}
          {item.completedBy &&
            `Signed off by ${item.completedBy} · ${formatUtc(item.completedUtc)}.`}
        </p>
      )}

      {moves.length > 0 && (
        <div className="row-actions" role="group" aria-label={`Move ${item.title}`}>
          {moves.map((m) => (
            <button
              key={m.to}
              type="button"
              className={
                m.to === 'Completed' || m.to === 'Submitted'
                  ? 'btn btn-primary btn-sm'
                  : 'btn btn-sm'
              }
              disabled={setStatus.isPending}
              onClick={() => (m.needsReason ? setAsking(m) : move(m))}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {asking && (
        <form
          className="form"
          aria-label={`Reason to ${asking.label.toLowerCase()}`}
          onSubmit={(e) => {
            e.preventDefault()
            if (reason.trim()) move(asking, reason.trim())
          }}
        >
          <div className="field">
            <label>
              {asking.to === 'NeedsRevision'
                ? 'What needs to change? The responder sees this.'
                : 'Why does this not apply? Every line gets an answer, even when the answer is "this does not exist".'}
              <textarea
                className="request-input"
                rows={2}
                maxLength={1000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                autoFocus
              />
            </label>
          </div>
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={!reason.trim() || setStatus.isPending}
            >
              {asking.label}
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setAsking(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {error && <p className="error-text small">{error}</p>}

      {room && (
        <ItemAssignees
          roomId={roomId}
          item={item}
          editable={
            checklist.canAssign === true && (roomStatus === 'Draft' || roomStatus === 'Open')
          }
        />
      )}
      <ItemDocuments roomId={roomId} item={item} />
      {room && checklist.canSubmit && !dataPolicyBlocks(room) && (
        <ItemUpload roomId={roomId} room={room} item={item} section={section} />
      )}
      {canReadHistory && <ItemHistory roomId={roomId} item={item} />}
    </div>
  )
}

function ItemDocuments({ roomId, item }: { roomId: string; item: ChecklistItemResponse }) {
  const documents = useDocumentsByChecklistItem(roomId, item.id)
  const folders = useFolders(roomId)
  return (
    <section aria-label={`Documents answering ${item.title}`}>
      <h4 className="h4">Documents answering this</h4>
      {documents.isPending && <p className="muted small">Loading…</p>}
      {documents.isError && <p className="error-text small">{errorMessage(documents.error)}</p>}
      {documents.data && documents.data.length === 0 && (
        <p className="muted small no-margin">
          None yet. Name this item when uploading, or tag a document from its panel. An item can
          also be answered in words and submitted with no document.
        </p>
      )}
      {documents.data && documents.data.length > 0 && (
        <DocumentList
          roomId={roomId}
          documents={documents.data}
          selected={null}
          onSelect={() => undefined}
          folders={folders.data ?? []}
        />
      )}
    </section>
  )
}
